import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { zodValidator, fallback } from "@tanstack/zod-adapter";
import { z } from "zod";
import { listMessageReports, resolveMessageReport, adminDeleteShiftMessage } from "@/lib/messages.functions";
import { getIsAdmin } from "@/lib/payments.functions";
import { ReserveHeader } from "@/components/ReserveHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const searchSchema = z.object({
  status: fallback(z.enum(["open", "reviewed", "dismissed", "actioned", "all"]), "open").default("open"),
  q: fallback(z.string(), "").default(""),
  reporter: fallback(z.enum(["all", "venue", "bartender", "admin"]), "all").default("all"),
  category: fallback(z.enum(["all", "abuse", "spam", "harassment", "inappropriate", "other"]), "all").default("all"),
});

export const Route = createFileRoute("/_authenticated/admin/message-reports")({
  head: () => ({ meta: [{ title: "Reported Messages — The Hospitality Reserve" }] }),
  validateSearch: zodValidator(searchSchema),
  component: AdminMessageReports,
});

type ReportRow = {
  id: string; message_id: string; shift_id: string; reporter_user_id: string;
  reporter_role: string; category: string; details: string | null;
  status: string; resolution_notes: string | null; resolved_at: string | null;
  resolved_by: string | null; created_at: string;
  message: {
    id: string; body: string; sender_role: string; sender_id: string;
    attachment_name: string | null; attachment_mime: string | null;
    deleted_at: string | null; created_at: string;
  } | null;
};

const TABS = ["open", "reviewed", "dismissed", "actioned", "all"] as const;
const CATEGORIES = ["all", "abuse", "spam", "harassment", "inappropriate", "other"] as const;
const ROLES = ["all", "venue", "bartender", "admin"] as const;

function AdminMessageReports() {
  const navigate = useNavigate({ from: "/admin/message-reports" });
  const search = Route.useSearch();
  const checkAdmin = useServerFn(getIsAdmin);
  const load = useServerFn(listMessageReports);
  const resolve = useServerFn(resolveMessageReport);
  const remove = useServerFn(adminDeleteShiftMessage);

  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState<ReportRow[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [notes, setNotes] = useState<Record<string, string>>({});

  // Local input state so typing is smooth; URL sync is debounced.
  const [queryInput, setQueryInput] = useState(search.q);

  const refresh = async () => {
    setLoading(true);
    const r = await load({
      data: {
        status: search.status,
        q: search.q,
        reporter: search.reporter,
        category: search.category,
        limit: 200,
      },
    });
    if ("error" in r) toast.error(r.error); else setRows(r.rows as ReportRow[]);
    setLoading(false);
  };

  useEffect(() => {
    (async () => {
      const a = await checkAdmin();
      if (!a.isAdmin) { navigate({ to: "/dashboard", replace: true }); return; }
      await refresh();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync input from URL (browser back/forward, reset).
  useEffect(() => {
    setQueryInput(search.q);
  }, [search.q]);

  // Sync input to URL after a short pause.
  useEffect(() => {
    const t = setTimeout(() => {
      if (queryInput !== search.q) {
        navigate({ search: (prev: typeof search) => ({ ...prev, q: queryInput }), replace: true });
      }
    }, 300);
    return () => clearTimeout(t);
  }, [queryInput, search.q, navigate]);

  // Refetch when any filter changes.
  useEffect(() => {
    if (!loading) void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search.status, search.reporter, search.category, search.q]);

  const setFilter = (patch: Partial<typeof search>) => {
    navigate({ search: (prev: typeof search) => ({ ...prev, ...patch }), replace: true });
  };

  const resetFilters = () => {
    navigate({ search: { status: "open" }, replace: true });
    setQueryInput("");
  };

  const act = async (row: ReportRow, action: "dismiss" | "review" | "action") => {
    setBusy(row.id);
    try {
      const r = await resolve({ data: { report_id: row.id, action, notes: notes[row.id] ?? "" } });
      if ("error" in r) throw new Error(r.error);
      if (action === "action") {
        const reason = (notes[row.id] ?? "").trim() || `Removed via report ${row.id.slice(0, 8)}`;
        const d = await remove({ data: { message_id: row.message_id, reason } });
        if ("error" in d) toast.error(`Resolved but removal failed: ${d.error}`);
      }
      toast.success("Report updated.");
      await refresh();
    } catch (e) { toast.error(e instanceof Error ? e.message : "Failed."); }
    finally { setBusy(null); }
  };

  const hasFilters = search.q || search.reporter !== "all" || search.category !== "all";

  return (
    <div className="min-h-screen bg-background">
      <ReserveHeader role="admin" />
      <main className="mx-auto max-w-5xl px-6 py-10">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="tracking-wide-luxury text-accent">Admin</p>
            <h1 className="mt-3 font-display text-3xl">Reported Messages</h1>
            <p className="mt-2 text-sm text-muted-foreground">Abuse and spam reports from venues and bartenders.</p>
          </div>
          <Link to="/admin" className="border border-border px-4 py-2 text-xs tracking-wide-luxury hover:border-accent">Back</Link>
        </div>
        <span className="gold-divider mt-4" />

        <div className="mt-6 flex flex-col gap-3 lg:flex-row lg:items-center">
          <div className="flex-1">
            <Input
              value={queryInput}
              onChange={(e) => setQueryInput(e.target.value.slice(0, 200))}
              placeholder="Search reporter, message, attachment, shift…"
              className="rounded-none"
            />
          </div>
          <div className="flex flex-wrap gap-2">
            <Select value={search.reporter} onValueChange={(v) => setFilter({ reporter: v as typeof search.reporter })}>
              <SelectTrigger className="w-[140px] rounded-none">
                <SelectValue placeholder="Reporter" />
              </SelectTrigger>
              <SelectContent>
                {ROLES.map((r) => (
                  <SelectItem key={r} value={r} className="capitalize">{r}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={search.category} onValueChange={(v) => setFilter({ category: v as typeof search.category })}>
              <SelectTrigger className="w-[160px] rounded-none">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                {CATEGORIES.map((c) => (
                  <SelectItem key={c} value={c} className="capitalize">{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {hasFilters && (
              <Button onClick={resetFilters} variant="outline" className="rounded-none tracking-wide-luxury">Reset</Button>
            )}
          </div>
        </div>

        <div className="mt-4 flex flex-wrap gap-2 border-b border-border">
          {TABS.map((t) => (
            <button key={t} onClick={() => setFilter({ status: t })}
              className={`px-4 py-2 text-xs tracking-wide-luxury capitalize ${search.status === t ? "border-b-2 border-accent text-foreground" : "text-muted-foreground"}`}>
              {t}
            </button>
          ))}
        </div>

        {loading ? (
          <p className="mt-12 text-center text-sm text-muted-foreground">Loading…</p>
        ) : rows.length === 0 ? (
          <p className="mt-8 text-sm text-muted-foreground">No reports match these filters.</p>
        ) : (
          <div className="mt-6 space-y-3">
            {rows.map((r) => (
              <div key={r.id} className="border border-border bg-card p-4">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <p className="text-xs tracking-wide-luxury text-destructive">{r.category.toUpperCase()} · {r.status.toUpperCase()}</p>
                  <p className="text-xs text-muted-foreground">{new Date(r.created_at).toLocaleString()}</p>
                </div>
                <p className="mt-1 text-xs text-muted-foreground">
                  Reporter {r.reporter_role} · {r.reporter_user_id.slice(0, 8)} · Shift {r.shift_id.slice(0, 8)}
                </p>
                {r.details && <p className="mt-2 text-sm"><span className="text-muted-foreground">Note:</span> {r.details}</p>}
                {r.message && (
                  <div className="mt-3 border-l-2 border-border pl-3">
                    <p className="text-xs text-muted-foreground">
                      Message by {r.message.sender_role} · {new Date(r.message.created_at).toLocaleString()}
                      {r.message.deleted_at && <span className="ml-2 text-destructive">[already removed]</span>}
                    </p>
                    {r.message.body && <p className={`mt-1 text-sm whitespace-pre-wrap ${r.message.deleted_at ? "italic text-muted-foreground" : ""}`}>{r.message.body}</p>}
                    {r.message.attachment_name && (
                      <p className="mt-1 text-xs text-muted-foreground">📎 {r.message.attachment_name} ({r.message.attachment_mime})</p>
                    )}
                  </div>
                )}
                {r.status === "open" ? (
                  <div className="mt-3 space-y-2">
                    <Textarea value={notes[r.id] ?? ""} onChange={(e) => setNotes((n) => ({ ...n, [r.id]: e.target.value.slice(0, 500) }))}
                      rows={2} placeholder="Resolution notes (optional, required for 'action')…" className="rounded-none" />
                    <div className="flex flex-wrap gap-2">
                      <Button onClick={() => act(r, "dismiss")} disabled={busy === r.id} variant="outline" className="rounded-none tracking-wide-luxury">Dismiss</Button>
                      <Button onClick={() => act(r, "review")} disabled={busy === r.id} variant="outline" className="rounded-none tracking-wide-luxury">Mark reviewed</Button>
                      <Button onClick={() => act(r, "action")} disabled={busy === r.id || !!r.message?.deleted_at}
                        className="rounded-none bg-destructive text-white hover:bg-destructive/90 tracking-wide-luxury">
                        Remove message & action
                      </Button>
                      <Link to="/shift/$id/notes" params={{ id: r.shift_id }}
                        className="border border-border px-4 py-2 text-xs tracking-wide-luxury hover:border-accent">View thread →</Link>
                    </div>
                  </div>
                ) : (
                  <div className="mt-3 text-xs text-muted-foreground">
                    {r.resolved_at && <>Resolved {new Date(r.resolved_at).toLocaleString()}{r.resolved_by ? ` by ${r.resolved_by.slice(0, 8)}` : ""}</>}
                    {r.resolution_notes && <p className="mt-1">Notes: {r.resolution_notes}</p>}
                    <Link to="/shift/$id/notes" params={{ id: r.shift_id }}
                      className="mt-2 inline-block text-accent hover:underline">View thread →</Link>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
